import { useCallback, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { weddingQuoteSchema, type QuoteService, type WeddingQuoteInput } from "@/lib/plan-wizard-schema";
import { useSubmitWeddingQuote } from "@/hooks/useSubmitEvent";
import { usePartialLeadCapture, usePlanMenuRedirect } from "@/hooks/usePlanLeadFlow";
import { QUOTE_BRANCH_CONFIG } from "./quote-branch-config";
import { EventTypeChips } from "./EventTypeChips";
import { FulfillmentChoice } from "./FulfillmentChoice";
import { QuotePreferencesFields } from "./QuotePreferencesFields";

interface Props {
  service: QuoteService;
  menuId?: string | null;
}

/** The quote form for wedding, live service, corporate and drop-off (#1221).
 * Form first: on submit the visitor goes straight to their service's menu. */
export function WeddingQuoteBranch({ service, menuId }: Props) {
  const cfg = QUOTE_BRANCH_CONFIG[service];
  const submit = useSubmitWeddingQuote();
  const goToMenu = usePlanMenuRedirect(service);
  // Synchronous guard so a double submit (Enter + click, StrictMode) can't create
  // a duplicate completed event / double-fire side effects. Reset on error.
  const submittedRef = useRef(false);

  const form = useForm<WeddingQuoteInput>({
    resolver: zodResolver(weddingQuoteSchema),
    defaultValues: {
      service,
      event_type_name: cfg.defaultEventTypeName,
      event_date: "",
      guest_count: 0,
      venue_location: "",
      fulfillment: undefined,
      dietary: [],
      client_name: "",
      client_email: "",
      client_phone: "",
      notes: "",
    },
  });
  const fulfillment = form.watch("fulfillment");
  const locationOptional = service === "drop_off" && fulfillment === "pickup";

  // Partial capture (#226) on name/email blur.
  const getContact = useCallback(
    () => ({
      client_name: form.getValues("client_name"),
      client_email: form.getValues("client_email"),
      client_phone: form.getValues("client_phone"),
    }),
    [form],
  );
  const partial = usePartialLeadCapture(getContact, { event_type: service });

  const onSubmit = form.handleSubmit(async (values) => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    const partialEventId = await partial.awaitId();
    submit.mutate(
      { ...values, menu_id: menuId ?? null, partial_event_id: partialEventId },
      {
        // Tracking already fired inside the mutation (persistCompletedEvent).
        onSuccess: (result) => goToMenu(result, values.client_name),
        onError: (e) => {
          submittedRef.current = false;
          toast.error(e.message || "Submission failed. Please try again.");
        },
      }
    );
  });

  return (
    <div className="max-w-2xl mx-auto space-y-8">
      <div className="text-center">
        <h2 className="font-logo text-2xl sm:text-3xl font-semibold tracking-tight">{cfg.heading}</h2>
        <p className="mt-2 text-sm text-muted-foreground max-w-md mx-auto leading-relaxed">{cfg.subheading}</p>
      </div>

      <form onSubmit={onSubmit} className="space-y-6">
        {/* Event details */}
        <Card>
          <CardContent className="pt-6 space-y-4">
            <SectionLabel step={1} title="Event details" />
            <div className="grid gap-4">
              {cfg.showFulfillment && (
                <FulfillmentChoice control={form.control} error={form.formState.errors.fulfillment?.message} />
              )}
              {cfg.showEventTypeChips && (
                <EventTypeChips control={form.control} error={form.formState.errors.event_type_name?.message} />
              )}
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="grid gap-1.5">
                  <Label htmlFor="w-date">{cfg.dateLabel}</Label>
                  <Input
                    id="w-date"
                    type="date"
                    {...form.register("event_date")}
                    aria-invalid={!!form.formState.errors.event_date}
                    aria-describedby={form.formState.errors.event_date ? "w-date-error" : undefined}
                  />
                  {form.formState.errors.event_date && (
                    <p id="w-date-error" role="alert" className="text-xs text-destructive">{form.formState.errors.event_date.message}</p>
                  )}
                </div>
                <div className="grid gap-1.5">
                  <Label htmlFor="w-guests">{cfg.guestsLabel}</Label>
                  <Input
                    id="w-guests"
                    type="number"
                    min={1}
                    placeholder="e.g. 120"
                    {...form.register("guest_count")}
                    aria-invalid={!!form.formState.errors.guest_count}
                    aria-describedby={form.formState.errors.guest_count ? "w-guests-error" : undefined}
                  />
                  {form.formState.errors.guest_count && (
                    <p id="w-guests-error" role="alert" className="text-xs text-destructive">{form.formState.errors.guest_count.message}</p>
                  )}
                </div>
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="w-venue">
                  {cfg.locationLabel}
                  {locationOptional && <span className="font-normal text-muted-foreground"> (optional)</span>}
                </Label>
                <Input
                  id="w-venue"
                  placeholder={cfg.locationPlaceholder}
                  {...form.register("venue_location")}
                  aria-invalid={!!form.formState.errors.venue_location}
                  aria-describedby={form.formState.errors.venue_location ? "w-venue-error" : undefined}
                />
                {form.formState.errors.venue_location && (
                  <p id="w-venue-error" role="alert" className="text-xs text-destructive">{form.formState.errors.venue_location.message}</p>
                )}
                <p className="text-xs text-muted-foreground">{cfg.locationHint}</p>
              </div>

              {cfg.showPreferences && <QuotePreferencesFields control={form.control} register={form.register} />}
            </div>
          </CardContent>
        </Card>

        {/* Contact info */}
        <Card>
          <CardContent className="pt-6 space-y-4">
            <SectionLabel step={2} title="Your contact info" />
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="grid gap-1.5">
                <Label htmlFor="w-name">Name</Label>
                <Input
                  id="w-name"
                  placeholder="First and last name"
                  {...form.register("client_name", { onBlur: partial.maybeSavePartial })}
                  aria-invalid={!!form.formState.errors.client_name}
                  aria-describedby={form.formState.errors.client_name ? "w-name-error" : undefined}
                />
                {form.formState.errors.client_name && (
                  <p id="w-name-error" role="alert" className="text-xs text-destructive">{form.formState.errors.client_name.message}</p>
                )}
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="w-email">Email</Label>
                <Input
                  id="w-email"
                  type="email"
                  placeholder="you@example.com"
                  {...form.register("client_email", { onBlur: partial.maybeSavePartial })}
                  aria-invalid={!!form.formState.errors.client_email}
                  aria-describedby={form.formState.errors.client_email ? "w-email-error" : undefined}
                />
                {form.formState.errors.client_email && (
                  <p id="w-email-error" role="alert" className="text-xs text-destructive">{form.formState.errors.client_email.message}</p>
                )}
              </div>
              <div className="grid gap-1.5 sm:col-span-2">
                <Label htmlFor="w-phone">Phone</Label>
                <Input
                  id="w-phone"
                  type="tel"
                  autoComplete="tel"
                  inputMode="tel"
                  placeholder="(416) 555-0000"
                  {...form.register("client_phone")}
                  aria-invalid={!!form.formState.errors.client_phone}
                  aria-describedby={form.formState.errors.client_phone ? "w-phone-error" : undefined}
                />
                {form.formState.errors.client_phone && (
                  <p id="w-phone-error" role="alert" className="text-xs text-destructive">{form.formState.errors.client_phone.message}</p>
                )}
              </div>
              <div className="grid gap-1.5 sm:col-span-2">
                <Label htmlFor="w-notes">Anything else? <span className="font-normal text-muted-foreground">(optional)</span></Label>
                <Textarea
                  id="w-notes"
                  rows={3}
                  placeholder="Timeline, menu ideas, dietary needs, inspiration…"
                  {...form.register("notes")}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex flex-col items-center gap-3 pt-2">
          <Button
            type="submit"
            size="lg"
            className="w-full sm:w-auto min-w-[240px] min-h-[48px] text-base"
            disabled={submit.isPending}
          >
            {submit.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {submit.isPending ? "Submitting…" : cfg.submitLabel}
          </Button>
          <p className="text-xs text-muted-foreground text-center max-w-md">
            We'll follow up within 24 hours. By submitting, you agree we may contact you about your
            event by email or text.
            Unsubscribe or reply STOP anytime.
          </p>
        </div>
      </form>
    </div>
  );
}

function SectionLabel({ step, title }: { step: number; title: string }) {
  return (
    <div className="flex items-center gap-3">
      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
        {step}
      </span>
      <h3 className="text-base font-semibold tracking-tight">{title}</h3>
      <span className="ml-auto text-xs text-muted-foreground">Step {step} of 2</span>
    </div>
  );
}
